/**
 * JS-controlled lazy image loader.
 *
 * Replaces the browser's native ~1200 px lazy-loading threshold with a
 * tighter 200 px rootMargin, so images just below the fold don't load
 * prematurely. Works alongside snippet.image.liquid which renders lazy
 * images with data-src / data-srcset attributes and a 1×1 placeholder src.
 *
 * Eager images (fetchpriority="high") are untouched — they use native
 * loading="eager" and remain on the browser's fast path.
 */

const PLACEHOLDER =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/** Distance outside the viewport at which we start loading images. */
const ROOT_MARGIN = '200px 0px';

/**
 * Swap data-srcset → srcset on <source> elements and data-src → src on
 * the <img> inside a <picture data-lazy-picture> element.
 */
function activatePicture(picture) {
  picture.querySelectorAll('source[data-srcset]').forEach((source) => {
    source.srcset = source.dataset.srcset;
    source.removeAttribute('data-srcset');
  });

  const img = picture.querySelector('img[data-src]');
  if (img) {
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  }

  picture.removeAttribute('data-lazy-picture');
}

/**
 * Initialise the observer. Called once on DOMContentLoaded and again
 * whenever new sections are injected (Shopify theme editor).
 *
 * @param {Element} [scope=document] – subtree to search for lazy pictures.
 */
export function initLazyImages(scope = document) {
  const pictures = scope.querySelectorAll('picture[data-lazy-picture]');
  if (!pictures.length) return;

  // No IntersectionObserver support (very old browsers) — load everything now.
  if (!('IntersectionObserver' in window)) {
    pictures.forEach(activatePicture);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          activatePicture(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: ROOT_MARGIN }
  );

  pictures.forEach((picture) => observer.observe(picture));
}
